import { useState, useRef, useEffect } from 'react'
import { RadioButtonChangeParams } from 'primereact/radiobutton'

// local imports
import GymService from 'service/GymService'
import { GymListTypes } from 'types/GymType'
import useDidMountEffect from 'utils/useDidMountEffect'

interface HookReturnTypes<T> {
  listType: GymListTypes
  handleChangeListType: (e: RadioButtonChangeParams) => void
  gymList: T[] | []
  getGymList: (data: any) => void
  loading: boolean
}

export default function useGetGymFilteredList<T>(
  defaultValues: any,
  handleResetFilterForm: () => void,
  isLoading: boolean
): HookReturnTypes<T> {
  /* Local States */
  const [listType, setListType] = useState<GymListTypes>('personal')
  const [gymList, setGymList] = useState<T[] | []>([])
  const [loading, setLoading] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  /* ######## Get GYM Salon Lists (Gym Salon, Group Lessons, Trainer List) ######## */

  function fetchLists(data: any) {
    if (listType === 'personal') {
      return GymService.getFilteredGymSalonList<T>({
        InventoryIdList: [],
        GymServiceId: 0,
        IsEmployeeLocation: false,
        IsIncludeNotActives: false,
        ...data, // LocationId - ReservationDate
      })
    } else if (listType === 'group') {
      return GymService.getFilteredGymGroupList<T>({
        GymGroupLessonId: 0,
        LessonTypeIdList: [],
        GymServiceId: 0,
        IsEmployeeLocation: false,
        IsIncludeNotActives: false,
        ...data, // LocationId - ReservationDate
      })
    } else {
      return GymService.getFilteredGymTrainerList<T>({
        GymTrainerId: 0,
        GymTrainerServiceId: 0,
        TrainerServiceTypeIdList: [],
        GymServiceId: 0,
        IsEmployeeLocation: false,
        IsIncludeNotActives: false,
        ...data, // LocationId - ReservationDate
      })
    }
  }

  function getGymList(data: any) {
    if (!isMountedRef.current) return
    setLoading(true)
    fetchLists(data)
      .then((r) => {
        if (isMountedRef.current) {
          setGymList(r.data)
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false)
        }
      })
  }

  const handleChangeListType = (e: RadioButtonChangeParams) => {
    if (e.value === listType) return
    setGymList([])
    setListType(e.value)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => getGymList(defaultValues), [isLoading])

  /* Run effects after changing listType Radio value */
  useDidMountEffect(() => {
    handleResetFilterForm()
    getGymList(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listType])

  return { listType, handleChangeListType, gymList, getGymList, loading }
}
